import { useEffect, useRef, useState, useCallback } from "react";

/**
 * useUnsavedGuard — combines two behaviours the Data Editor's form modals need:
 *
 *   1. Debounced auto-save. Fires `onSave(payload)` `debounceMs` after the
 *      last mutation to `payload`. Skips the initial mount so we don't
 *      save a freshly-opened form.
 *   2. Escape-key confirmation. If the form has unsaved changes (i.e. a
 *      pending debounce OR a manual dirty flag) then Escape pops a
 *      confirmation dialog offering **Save**, **Discard**, or **Keep
 *      editing** instead of silently closing the modal.
 *
 * Returns:
 *   {
 *     dirty,             // bool — user has typed since the last save
 *     confirming,        // "esc" | null — should the confirmation UI show?
 *     scheduleSave,      // () => void — call from any onChange handler
 *     saveNow,           // async () => await onSave(latest payload)
 *     handleClose,       // wrap this into the modal's close button
 *     confirmSave,       // async () — used by the confirm dialog
 *     confirmDiscard,    // () — used by the confirm dialog
 *     confirmKeep,       // () — dismiss the confirmation
 *   }
 *
 * The hook is *pure UI plumbing* — the caller decides what "payload" means,
 * so we stay flexible across the six different form modals in the app.
 */
export default function useUnsavedGuard({
  open,
  getPayload,
  onSave,
  onClose,
  debounceMs = 2000,
  enabled = true,
  isDirty,   // optional — when provided, the hook uses this bool instead of
             // deriving dirtiness from scheduleSave calls. Lets callers wire
             // the guard without instrumenting every onChange handler.
}) {
  const [dirtyInternal, setDirtyInternal] = useState(false);
  const dirty = typeof isDirty === "boolean" ? isDirty : dirtyInternal;
  const [confirming, setConfirming] = useState(null);
  const debounceTimer = useRef(null);
  // Latest payload getter — captured via ref so the timer callback always
  // reads the freshest state without needing to re-arm on every change.
  const getPayloadRef = useRef(getPayload);
  useEffect(() => { getPayloadRef.current = getPayload; }, [getPayload]);
  const onSaveRef = useRef(onSave);
  useEffect(() => { onSaveRef.current = onSave; }, [onSave]);
  // Also mirror `enabled` and `debounceMs` into refs. The timer callback
  // that fires 2s after the last edit reads these — if they change mid-
  // debounce (e.g. modal calls setEnabled(false) after starting to save),
  // we want the callback to see the newest value, not a stale closure.
  const enabledRef = useRef(enabled);
  useEffect(() => { enabledRef.current = enabled; }, [enabled]);
  const debounceMsRef = useRef(debounceMs);
  useEffect(() => { debounceMsRef.current = debounceMs; }, [debounceMs]);

  // Reset state whenever the modal closes/re-opens so a fresh open never
  // inherits a dirty flag from the last edit session.
  useEffect(() => {
    if (!open) {
      setConfirming(null);
      if (debounceTimer.current) {
        // Closed via a path that bypassed handleClose — save, don't drop.
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
        saveNowRef.current?.();
      } else {
        setDirtyInternal(false);
      }
    }
  }, [open]);

  const saveNow = useCallback(async () => {
    if (!enabledRef.current) return;
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
    try {
      const payload = getPayloadRef.current?.();
      if (payload == null) return;
      await onSaveRef.current?.(payload);
      setDirtyInternal(false);
      // Any recovery — clear the global "offline / save failed" banner.
      window.dispatchEvent(new CustomEvent("app:save-ok"));
    } catch (err) {
      // Leave dirty=true so the user can retry; broadcast a global toast so
      // any surface (top-level app frame) can surface a banner. Distinguish
      // "we appear to be offline" from a real server error.
      const offline = typeof navigator !== "undefined" && navigator.onLine === false;
      window.dispatchEvent(new CustomEvent("app:save-failed", {
        detail: {
          offline,
          reason: err?.response?.data?.detail || err?.message || "Save failed",
          retry: saveNow,
        },
      }));
    }
  }, []);
  const saveNowRef = useRef(saveNow);
  saveNowRef.current = saveNow;

  const scheduleSave = useCallback(() => {
    if (!enabledRef.current) return;
    setDirtyInternal(true);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      debounceTimer.current = null;
      saveNow();
    }, debounceMsRef.current);
  }, [saveNow]);

  // Intercept Escape only when dirty — clean forms close instantly like before.
  useEffect(() => {
    if (!open || !enabled) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (!dirty) return;  // useEscClose handles the clean-form path
      e.stopPropagation();
      e.preventDefault();
      setConfirming("esc");
    };
    // Capture-phase so we run BEFORE useEscClose picks up the same event.
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, enabled, dirty]);

  const handleClose = useCallback(() => {
    if (!enabledRef.current || !dirty) {
      onClose?.();
      return;
    }
    setConfirming("esc");
  }, [dirty, onClose]);

  const confirmSave = useCallback(async () => {
    setConfirming(null);
    await saveNow();
    onClose?.();
  }, [saveNow, onClose]);

  const confirmDiscard = useCallback(() => {
    setConfirming(null);
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
    setDirtyInternal(false);
    onClose?.();
  }, [onClose]);

  const confirmKeep = useCallback(() => setConfirming(null), []);

  return {
    dirty,
    confirming,
    scheduleSave,
    saveNow,
    handleClose,
    confirmSave,
    confirmDiscard,
    confirmKeep,
  };
}
